import { Box, GlobalStyles, alpha, useTheme } from '@mui/material';
import type { TIconComponent } from 'components/Icon/registry';
import { useLogoContrastStyle } from 'hooks/useLogoContrastStyle';
import { partnerAssets } from 'partners/partnersAssets';
import { useEffect, useState } from 'react';
import { TProps_InstaSpinner } from 'typings';

const RING_SIZES = [68, 52, 36];
const RING_THICKNESS_PX = 1.5;

/**
 * Matches the reference recording (Downloads/appLoader.MP4): THREE
 * concentric rings, each growing clockwise from a dot at top-centre (0, r)
 * into a full closed circle, holding there, then shrinking back down to a
 * dot at that SAME point — always moving clockwise, never reversing, and
 * always resetting at the top. Not MUI's default indeterminate progress
 * animation (never fully closes, cycles continuously), and not a plain
 * `stroke-dashoffset: circumference → 0 → circumference` sweep either —
 * that alone peels the arc back the way it grew, which in the FIXED
 * (unrotated) reference frame reads as the tail retreating
 * counter-clockwise back the way it came.
 *
 * The fix pairs the dash animation with a `transform: rotate()` that
 * exactly cancels that local retreat: from 60%-85% (the shrink window)
 * `strokeDashoffset` retreats by one full circumference while `transform`
 * simultaneously advances by exactly 360deg in the SAME keyframe stops (so
 * both interpolate with identical easing/pacing) — the retreating tail's
 * ABSOLUTE screen position stays fixed at the top the whole time, while the
 * HEAD (fixed locally at the path start) sweeps forward a full clockwise
 * lap to meet it there, closing the arc to a dot exactly at top rather
 * than wherever the local geometry happens to land. All three rings run
 * off one shared `@keyframes` (`SWEEP_ANIMATION_NAME`/`SWEEP_DURATION_MS`
 * below, injected once via `GlobalStyles`) at the SAME percentages/
 * duration, each ring only supplying its own `--ring-circumference`
 * custom property, so they can't drift apart the way three separately-
 * driven animations did.
 */
const SWEEP_ANIMATION_NAME = 'insta-spinner-sweep';
const SWEEP_DURATION_MS = 2400;
// -90deg is the same "move the SVG's default 3-o'clock stroke start to
// 12 o'clock" offset as before, held flat through the whole grow+hold
// window (0%-60% — no compensation needed there, see the comment above).
// 270deg (exactly one full turn further) is held flat through 85%-100%,
// the compensation reached by the end of the shrink window. 0% and 100%
// use DIFFERENT values (-90deg / 270deg) even though every iteration
// looks identical — the jump between them at the loop point is invisible
// since the two angles are the same position (270 ≡ -90 mod 360), but
// each needs its OWN literal value so 0%-20% and 85%-100% each stay flat
// rather than animating a spurious extra turn.
const SWEEP_KEYFRAMES = {
  [`@keyframes ${SWEEP_ANIMATION_NAME}`]: {
    '0%': {
      strokeDashoffset: 'var(--ring-circumference)',
      transform: 'rotate(-90deg)'
    },
    '20%, 60%': { strokeDashoffset: 0, transform: 'rotate(-90deg)' },
    '85%, 100%': {
      strokeDashoffset: 'var(--ring-circumference)',
      transform: 'rotate(270deg)'
    }
  }
};

function Ring({ size, color }: { size: number; color: string }) {
  const outerSize = RING_SIZES[0];
  const radius = (size - RING_THICKNESS_PX) / 2;
  const circumference = 2 * Math.PI * radius;
  return (
    <Box
      component="svg"
      width={size}
      height={size}
      sx={{
        position: 'absolute',
        top: (outerSize - size) / 2,
        left: (outerSize - size) / 2,
        animation: `${SWEEP_ANIMATION_NAME} ${SWEEP_DURATION_MS}ms cubic-bezier(0.45, 0, 0.15, 1) infinite`,
        ['--ring-circumference' as string]: `${circumference}px`
      }}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={RING_THICKNESS_PX}
        strokeLinecap="round"
        strokeDasharray={circumference}
      />
    </Box>
  );
}

// Always white — the loader must read the same regardless of theme mode or
// product colour, not follow `text.primary` (which flips dark/light with the
// theme mode).

function Spinner() {
  const muiTheme = useTheme();
  return (
    <>
      <GlobalStyles styles={SWEEP_KEYFRAMES} />
      {RING_SIZES.map((size) => (
        <Ring key={size} size={size} color={muiTheme.palette.common.white} />
      ))}
    </>
  );
}

/** How long the mark takes to fade fully in — and the tint behind it to fade
 *  fully out — on mount. */
const REVEAL_MS = 500;

const OUTER_RING_SIZE = RING_SIZES[0];
const MARK_SIZE = 18;

function RecolouredMark({ Mark }: { Mark: TIconComponent }) {
  const [revealed, setRevealed] = useState(false);
  const muiTheme = useTheme();

  useEffect(() => {
    // Two rAFs: the first lets the initial (opacity 0) paint commit before
    // the transition starts, so the browser actually animates the reveal
    // instead of snapping straight to the end state.
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => setRevealed(true))
    );
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: MARK_SIZE,
        height: MARK_SIZE
      }}
    >
      {/* The reveal tint — fades to nothing as the mark fades in, rather
          than the mark simply popping in against a blank page. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: '-25%',
          borderRadius: '50%',
          backgroundColor: alpha(muiTheme.palette.common.white, 0.12),
          opacity: revealed ? 0 : 1,
          transition: `opacity ${REVEAL_MS}ms ease`
        }}
      />
      <Box
        role="img"
        aria-label="loader_icon"
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          color: muiTheme.palette.common.white,
          opacity: revealed ? 1 : 0,
          transition: `opacity ${REVEAL_MS}ms ease`,
          '& > svg': { width: '100%', height: '100%', display: 'block' }
        }}
      >
        <Mark />
      </Box>
    </Box>
  );
}

function InstaSpinner({ withWrapper = false }: TProps_InstaSpinner) {
  const logoContrastStyle = useLogoContrastStyle();
  // All partner assets — including this recolourable mark — live in one
  // place, `partnerAssets` (see partners/<partner>/assets.ts). A partner
  // without a `logoMarkIcon` (only homiq, currently) falls back to the
  // CDN `logoContrastIcon` as a plain <img> instead.
  const Mark = partnerAssets.logoMarkIcon;
  return (
    // `position: relative` + absolute-centering the ring cluster below,
    // rather than a flex `alignItems`/`justifyContent` pair centering it —
    // this box is routinely given its actual height by a PARENT's own
    // flex layout (e.g. InstaLoader's `height: 100dvh` column), which means
    // that centering would depend on percentage-height-on-a-flex-item
    // resolving correctly through two nested flex containers before it
    // even gets to align anything. `top/left 50% + translate(-50%,-50%)`
    // has no such dependency — it centers against this box's own resolved
    // height/width directly, on every rendering engine identically,
    // including older/embedded Android WebViews where that flex chain has
    // been the actual cause of the ring cluster rendering pinned to the
    // top instead of centered.
    <Box
      sx={{
        position: 'relative',
        height: withWrapper ? '60vh' : '100%',
        // The ring cluster below is `position: absolute`, so it no longer
        // contributes to this box's own auto-height the way an in-flow flex
        // child would. That's fine when a parent gives this box a definite
        // height to fill (InstaLoader's 100dvh, a Backdrop's full-viewport
        // box) — `height: 100%` resolves against it as normal. But the
        // "bare, no wrapper" usages inside a Popup's own shrink-to-fit Stack
        // (LoadingPopup and friends) don't provide a definite height at
        // all, so that percentage would resolve to `auto`, and with no
        // in-flow content left to size against, this box would collapse to
        // 0 and the "centered" ring cluster would end up pinned to its
        // zero-height top edge. `minHeight` keeps it at least the ring
        // cluster's own size in that case, matching what the old in-flow
        // version sized to there anyway.
        minHeight: OUTER_RING_SIZE,
        width: '100%'
      }}
    >
      {/* One shared containing block for the rings and the mark, so the
          mark is genuinely centred (absolute + translate) instead of
          eyeballed into place with a hand-tuned negative margin next to
          it in flex flow. */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: OUTER_RING_SIZE,
          height: OUTER_RING_SIZE
        }}
      >
        <Spinner />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -43%)'
          }}
        >
          {Mark ? (
            <RecolouredMark Mark={Mark} />
          ) : (
            <img
              src={partnerAssets.logoContrastIcon}
              alt="loader_icon"
              width={`${MARK_SIZE}px`}
              height={`${MARK_SIZE}px`}
              style={{ display: 'block', ...logoContrastStyle }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
export default InstaSpinner;
